/**
 * Helpers de apresentação do COFRE: chips de nível/ação de log, datas pt-BR
 * e mapeamento de códigos de erro conhecidos para mensagens amigáveis.
 */
import { Badge, type BadgeProps } from '@/components/ui/misc';
import { errorMessage } from '@/features/tickets/ticket-format';
import type { VaultLog, VaultPermissionLevel } from '@erp/shared';

export { errorMessage };

export const LEVEL_LABELS: Record<VaultPermissionLevel, string> = {
  read: 'Leitura',
  write: 'Edição',
};

/** Chip do meu nível no cofre: Leitura (neutro) / Edição (teal). */
export function LevelBadge({
  level,
  className,
}: {
  level: VaultPermissionLevel;
  className?: string;
}) {
  return (
    <Badge variant={level === 'write' ? 'primary' : 'default'} className={className}>
      {LEVEL_LABELS[level]}
    </Badge>
  );
}

const LOG_ACTION: Record<VaultLog['action'], { label: string; variant: BadgeProps['variant'] }> = {
  created: { label: 'Criada', variant: 'info' },
  updated: { label: 'Atualizada', variant: 'default' },
  revealed: { label: 'Revelada', variant: 'warning' },
  deleted: { label: 'Excluída', variant: 'destructive' },
};

export function LogActionBadge({ action }: { action: VaultLog['action'] }) {
  const chip = LOG_ACTION[action];
  return <Badge variant={chip.variant}>{chip.label}</Badge>;
}

export function entryCountLabel(count: number): string {
  return count === 1 ? '1 senha' : `${count} senhas`;
}

export function formatDateBR(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateTimeBR(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDateBR(d)} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Erros do cofre com mensagem dedicada; demais caem no errorMessage padrão. */
export function vaultErrorMessage(e: unknown): string {
  const code =
    e !== null && typeof e === 'object' && 'error' in e ? (e as { error: unknown }).error : null;
  if (code === 'vault_not_empty') return 'Esvazie o cofre antes de excluí-lo.';
  if (code === 'folder_not_empty') return 'Esvazie a pasta antes de excluí-la.';
  if (code === 'forbidden') return 'Você não tem permissão para esta ação.';
  return errorMessage(e);
}
